import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { toast } from "sonner";
import { Check, AlertTriangle, Copy, RefreshCw } from "lucide-react";

export default function AdminLogins() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/admin/login-status");
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFixAll = async () => {
    if (!window.confirm(
      "FIX ALL AUTHOR LOGINS now?\n\n" +
      "For every author this will:\n" +
      "  • Create a login user if one is missing\n" +
      "  • Make sure their role is 'author'\n" +
      "  • Make sure the email matches the author record\n" +
      "  • RESET their password to Welcome2026\n\n" +
      "After this, every author can log in with their email + Welcome2026.\n" +
      "Existing custom passwords will be lost."
    )) return;
    setFixing(true);
    try {
      const { data: res } = await api.post("/admin/authors/reset-passwords-to-default");
      toast.success(`✓ Fixed ${res.reset_count} author logins · created ${res.created_count} new accounts`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fix failed");
    } finally {
      setFixing(false);
    }
  };

  const emailBodyFor = (a) => {
    const first = (a.author_name || "there").split(" ")[0];
    return (
`Hi ${first},

Welcome to Healing House. I'm thrilled to share your brand-new Healing House Author Portal — a private, always-on window into your book's journey. It's our biggest step yet toward complete transparency for our authors, and I really hope you love it.

Inside your portal you'll see your live sales by market, your royalty snapshot, your payment history, and tailored AI coaching to help you keep championing your book. I hope it inspires you to share your work even more widely — every conversation, post, and recommendation moves the needle.

YOUR LOGIN DETAILS
  Portal:              ${data.portal_url}
  Email:               ${a.email}
  Temporary password:  ${data.default_password}

A NOTE ON ROYALTIES
  • Royalties are paid quarterly — each quarter falls due at the end of the following quarter and is paid within 30 days (Jan–Mar by 31 July, Apr–Jun by 31 October, Jul–Sep by 31 January, Oct–Dec by 30 April).
  • Please note IngramSpark typically reports sales to us ~90 days after they happen, so your portal will keep updating as new remittance data lands. Healing House direct sales appear immediately.

FEEDBACK & SUPPORT
  I'd genuinely love your feedback. If something feels off, missing, or could be clearer, please write to support@thebookdoula.com.au. Same email for any technical hiccups — we'll sort them quickly.

Once you're in, please change your password (top-right menu → Account & Password).

With love and gratitude for your work,
Vanessa Barrington
Healing House Publishing
`);
  };

  const copyOne = async (a) => {
    const ok = await copyToClipboard(emailBodyFor(a));
    toast[ok ? "success" : "error"](ok ? `Email for ${a.author_name} copied — paste into Gmail/Outlook` : "Couldn't copy");
  };

  const copyAll = async () => {
    const blocks = data.authors.map((a) => {
      const header = "──────────────────────────────────────────────\n" +
        `TO:   ${a.email}\n` +
        "SUBJ: Your new Healing House Author Portal — login inside\n" +
        "──────────────────────────────────────────────\n";
      return header + emailBodyFor(a);
    });
    const ok = await copyToClipboard(blocks.join("\n\n"));
    toast[ok ? "success" : "error"](ok ? "All author emails copied to clipboard" : "Couldn't copy");
  };

  if (loading) return <div className="p-8 text-hh-ink" data-testid="logins-loading">Loading login status…</div>;
  if (err) return (
    <div className="p-8 space-y-4" data-testid="logins-error">
      <h1 className="font-serif text-3xl text-hh-ink">Author Logins</h1>
      <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-900">
        <p className="font-medium mb-2">Couldn&rsquo;t load login status</p>
        <p className="font-mono text-xs">{err}</p>
      </div>
      <button onClick={load} className="hh-btn-outline">Retry</button>
    </div>
  );
  if (!data) return null;

  const broken = data.authors.filter((a) => !a.has_login || a.issue);

  return (
    <div className="space-y-8" data-testid="admin-logins-page">
      <header className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="hh-overline mb-3">Admin · Author Logins</p>
          <h1 className="font-serif text-4xl text-hh-ink tracking-tight mb-2">Who can log in</h1>
          <p className="text-hh-ink/70 text-sm max-w-3xl">
            Every author&rsquo;s login status at a glance. Click <strong>&ldquo;Fix all logins&rdquo;</strong> to
            guarantee every author can log in with <code className="bg-hh-muted px-1.5 rounded">Welcome2026</code> — it
            creates missing accounts, fixes roles, and force-resets passwords. Then copy each author&rsquo;s credentials
            and email them manually.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={load} className="hh-btn-outline" data-testid="refresh-logins">
            <RefreshCw size={14} className="inline mr-2" /> Refresh
          </button>
          <button onClick={copyAll} className="hh-btn-outline" data-testid="copy-all-creds">
            <Copy size={14} className="inline mr-2" /> Copy all credentials
          </button>
          <button onClick={onFixAll} disabled={fixing} className="hh-btn-primary" data-testid="fix-all-logins">
            {fixing ? "Fixing…" : "Fix all logins"}
          </button>
        </div>
      </header>

      {broken.length === 0 ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-900 flex items-center gap-3" data-testid="all-good">
          <Check size={18} /> All {data.authors.length} authors have working logins with the default password.
        </div>
      ) : (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900 flex items-start gap-3" data-testid="some-broken">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">{broken.length} of {data.authors.length} authors can&rsquo;t log in with the default password.</p>
            <p className="mt-1">Click <strong>&ldquo;Fix all logins&rdquo;</strong> to make them all work.</p>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-hh-border bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-hh-cream/60 text-hh-ink/70 text-left">
            <tr>
              <th className="px-5 py-3 font-semibold">Author</th>
              <th className="px-5 py-3 font-semibold">Login email</th>
              <th className="px-5 py-3 font-semibold">Password</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {data.authors.map((a) => {
              const ok = a.has_login && !a.issue;
              return (
                <tr key={a.author_id} className="border-t border-hh-border/60" data-testid={`login-row-${a.author_id}`}>
                  <td className="px-5 py-3 font-medium text-hh-ink">{a.author_name}</td>
                  <td className="px-5 py-3 font-mono text-xs text-hh-ink">{a.email}</td>
                  <td className="px-5 py-3 font-mono text-xs text-hh-ink">
                    {a.password_matches_default ? data.default_password : <span className="text-amber-700">(custom / unknown)</span>}
                  </td>
                  <td className="px-5 py-3">
                    {ok ? (
                      <span className="inline-flex items-center gap-1.5 text-emerald-700 text-xs"><Check size={14}/> Can log in</span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-amber-700 text-xs"><AlertTriangle size={14}/> {a.issue || "No login user"}</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => copyOne(a)}
                      className="text-xs px-2.5 py-1.5 rounded-md bg-hh-violet/10 hover:bg-hh-violet/20 text-hh-violet font-medium"
                      data-testid={`copy-creds-${a.author_id}`}
                    >Copy email</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="rounded-2xl border border-hh-border bg-hh-cream/30 p-5 text-sm text-hh-ink/80 space-y-2">
        <p><strong>How to use this page:</strong></p>
        <ol className="list-decimal pl-5 space-y-1">
          <li>Click <strong>&ldquo;Fix all logins&rdquo;</strong> &mdash; this guarantees every author can log in with <code className="bg-hh-muted px-1.5 rounded">Welcome2026</code>.</li>
          <li>Click <strong>&ldquo;Copy email&rdquo;</strong> next to each author &mdash; a complete, personalised welcome email (login details + EOFY/6-monthly royalty note + support contact) is copied to your clipboard.</li>
          <li>Open Gmail/Outlook, paste it into a new email, set the recipient and subject (suggestion: &ldquo;Your new Healing House Author Portal &mdash; login inside&rdquo;), and send.</li>
          <li>Or use <strong>&ldquo;Copy all author emails&rdquo;</strong> to grab every author&rsquo;s email at once with TO and SUBJ headers ready.</li>
          <li>Authors log in and can change their password from Account &amp; Password.</li>
        </ol>
      </div>
    </div>
  );
}
